import React from 'react';
import { Button } from '@/design-system/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/design-system/dropdown-menu';
import { Spinner } from '@/design-system/spinner';
import { cn } from '@/design-system/lib/utils';

export interface ActionDropdownAction {
  key: string;
  label: string;
  icon: string;
  onClick: () => void | Promise<void>;
  className?: string;
  separateAfter?: boolean;
  isLoading?: boolean;
  /** Destructive item (delete, cancel …): error-toned. */
  destructive?: boolean;
  /** Shown but not selectable (e.g. while another action of the row runs). */
  disabled?: boolean;
  /** Left out of the menu — lets a caller list every action and gate each. */
  hidden?: boolean;
}

interface ActionDropdownProps {
  /** The record/data object this dropdown is for (names the default aria-label). */
  record?: any;
  /**
   * Controlled open state. Leave `isOpen` and `onToggle` out and the menu
   * keeps its own — the usual case for a table row.
   */
  isOpen?: boolean;
  /** Function to toggle the dropdown state (with `isOpen`). */
  onToggle?: () => void;
  /** Array of actions to display in the dropdown */
  actions: ActionDropdownAction[];
  /** Optional aria label for accessibility */
  ariaLabel?: string;
  /** The row's action is running: the trigger spins and is disabled. */
  busy?: boolean;
  /** Optional className for the trigger button */
  buttonClassName?: string;
  /** Optional icon class for the trigger button */
  triggerIcon?: string;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  record,
  isOpen,
  onToggle,
  actions,
  ariaLabel,
  busy,
  buttonClassName,
  triggerIcon = 'ri-more-2-fill',
}) => {
  const [loadingKey, setLoadingKey] = React.useState<string | null>(null);
  const [ownOpen, setOwnOpen] = React.useState(false);
  const controlled = isOpen !== undefined && onToggle !== undefined;
  const open = controlled ? isOpen : ownOpen;
  const toggle = controlled ? onToggle : () => setOwnOpen((o) => !o);
  const isLoading = loadingKey !== null;
  const visible = actions.filter((a) => !a.hidden);

  const handleActionClick = async (action: ActionDropdownAction) => {
    if (isLoading) return;

    setLoadingKey(action.key);

    try {
      await action.onClick();
    } catch (error) {
      console.error(`Error executing action ${action.key}:`, error);
    } finally {
      setLoadingKey(null);
      // Close manually — auto-close was prevented so the menu could show
      // the loading state until the action settled.
      if (open) toggle();
    }
  };

  if (!visible.length) return null;

  const defaultAriaLabel =
    ariaLabel ||
    (record
      ? `Actions for ${record.name || record.platNomor || 'item'}`
      : 'Actions');

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next !== open) toggle();
      }}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        {/* Trigger only — the cell wrapping (sticky right, white bg, left
            border) is the table's responsibility, not ours. */}
        <Button
          tone="neutral"
          style="ghost"
          size="icon-sm"
          className={buttonClassName}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={defaultAriaLabel}
          aria-busy={busy || undefined}
          title={defaultAriaLabel}
          disabled={busy}
        >
          <i
            className={cn(
              'text-[16px]',
              busy ? 'ri-loader-4-line animate-spin' : triggerIcon
            )}
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="z-[99999] min-w-[14rem]"
        aria-label="Action menu"
        // Items are portaled, but React events still bubble to the row that
        // holds the dropdown: picking one must not also open the row.
        onClick={(e) => e.stopPropagation()}
      >
        {visible.map((action) => {
          const isActionLoading = loadingKey === action.key || action.isLoading;
          return (
            <React.Fragment key={action.key}>
              <DropdownMenuItem
                destructive={action.destructive}
                disabled={isLoading || action.isLoading || action.disabled}
                className={cn('cursor-pointer gap-2', action.className)}
                onSelect={(e) => {
                  // Keep the menu open until the (possibly async) action
                  // settles, then close manually.
                  e.preventDefault();
                  handleActionClick(action);
                }}
              >
                {isActionLoading ? (
                  <Spinner size="xs" tone="neutral" className="shrink-0" />
                ) : (
                  <i
                    className={cn(
                      action.icon,
                      'text-[15px]',
                      action.destructive ? '' : 'text-icon-sub-600'
                    )}
                    aria-hidden
                  />
                )}
                <span className="flex-1">{action.label}</span>
              </DropdownMenuItem>
              {action.separateAfter && <DropdownMenuSeparator />}
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionDropdown;
